import React, { useEffect, useState } from 'react';
import PageHeader from 'components/common/general/layouts/PageHeader';
import FileExchangeContent from 'components/files-tracking/file-exchange';
import GlobalLayout from 'components/common/general/layouts/GlobalLayout';
import FileDashboardSidebar from 'components/files-tracking/FileDashboardSidebar';
import { Book, DriveFile, Folder, NoteAdd } from '@insurancemenu/imenu-components-lib/dist/icons';
import ReadyToSendPdfs from 'components/files-tracking/ready-to-send-pdfs';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { Toast } from '@insurancemenu/imenu-components-lib';
import PolicyDashboard from 'components/files-tracking/policy';
import ReadyToSendEdis from 'components/files-tracking/ready-to-send-edi';
import { createStyles } from './styles';

export enum FileDashboardType {
  FILES_EXCHANGE = 'FILES_EXCHANGE',
  PDF_FILES_READY_TO_SEND = 'PDF_FILES_READY_TO_SEND',
  EDI_FILES_READY_TO_SEND = 'EDI_FILES_READY_TO_SEND',
  POLICY = 'POLICY',
}

export interface FileDashboardTitles {
  title: string;
  subtitle: string;
  content: JSX.Element;
  icon: JSX.Element;
}

export const FileDashboardContent: Record<FileDashboardType, FileDashboardTitles> = Object.freeze({
  [FileDashboardType.FILES_EXCHANGE]: {
    title: 'File Exchange',
    subtitle: "Sponsor's SFTP Integration Dashboard",
    content: <FileExchangeContent />,
    icon: <Folder width="2.4rem" height="2.4rem" />,
  },
  [FileDashboardType.PDF_FILES_READY_TO_SEND]: {
    title: 'PDF Files Ready To Send',
    subtitle: 'On this page, you will find the files that will be sent tonight.',
    content: <ReadyToSendPdfs />,
    icon: <DriveFile width="2.4rem" height="2.4rem" />,
  },
  [FileDashboardType.EDI_FILES_READY_TO_SEND]: {
    title: 'EDI Files Ready To Send',
    subtitle: 'On this page, you will find the files that will be sent tonight.',
    content: <ReadyToSendEdis />,
    icon: <NoteAdd width="2.4rem" height="2.4rem" />,
  },
  [FileDashboardType.POLICY]: {
    title: 'Policy',
    subtitle: 'Manage Policy Statuses',
    content: <PolicyDashboard />,
    icon: <Book width="2.4rem" height="2.4rem" />,
  },
});

const FilesTrackingPage = observer((): JSX.Element => {
  const classes = createStyles();
  const [dashboardType, setDashboardType] = useState(FileDashboardType.FILES_EXCHANGE);

  const {
    filesTrackingController: {
      toastInfo: { showToast, toastType, toastMessage },
      closeToast,
      getAllDomains,
    },
  } = useRootStore();

  useEffect(() => {
    void getAllDomains();
  }, []);

  const setActiveDashboardType = (e: React.SyntheticEvent<HTMLLIElement>): void => {
    const activeDashboardType = e.currentTarget.dataset.dashboardType;
    if (activeDashboardType) setDashboardType(activeDashboardType as FileDashboardType);
  };

  return (
    <>
      <GlobalLayout
        contentStyle={classes.mainContent}
        leftSection={
          <FileDashboardSidebar activeDashboardType={dashboardType} setDashboardType={setActiveDashboardType} />
        }
        rightSection={
          <>
            <PageHeader
              title={FileDashboardContent[dashboardType].title}
              subtitle={FileDashboardContent[dashboardType].subtitle}
            />
            {FileDashboardContent[dashboardType].content}
          </>
        }
      />
      <Toast show={showToast} onClose={closeToast} colorType={toastType}>
        {toastMessage}
      </Toast>
    </>
  );
});

export default FilesTrackingPage;
